import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  Plus,
  Users, 
  FileText, 
  DollarSign,
  TrendingUp,
  RefreshCw,
  Filter as FilterIcon,
  UserCog
} from 'lucide-react';
import { 
  CadastrarVendedor, 
  VendedoresList, 
  VendedorDetails, 
  VendedorFilters 
} from '@/components/Vendedores';
import { useVendedores } from '@/hooks/useVendedores';
import { VendedorFilters as VendedorFiltersType } from '@/types/vendedores';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { useIsMobile } from '@/hooks/use-mobile';

export default function Vendedores() {
  // Verificar se é admin para mostrar ações administrativas
  let authz;
  try {
    authz = useAuthorization();
  } catch (e) {
    console.error("❌ Vendedores: Erro ao obter contexto de autorização:", e);
    authz = null;
  }
  
  const isAdmin = authz?.hasRole?.('admin') || false;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendedor, setSelectedVendedor] = useState<string | null>(null);
  const [cadastroOpen, setCadastroOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<VendedorFiltersType>({
    periodo: '30_dias',
    status: 'todos',
    ordenacao: 'valor_contratos'
  });
  
  const { vendedores, loading, error, refetch } = useVendedores(filters);
  const isMobile = useIsMobile();

  // Filtrar vendedores por busca (nome, email)
  const filteredVendedores = vendedores?.filter(vendedor =>
    vendedor.vendedor_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendedor.vendedor_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Calcular métricas agregadas
  const metricsAgregadas = {
    totalVendedores: vendedores?.length || 0,
    vendedoresAtivos: vendedores?.filter(v => v.status).length || 0,
    totalParceiros: vendedores?.reduce((sum, v) => sum + v.total_parceiros, 0) || 0,
    parceirosAtivos: vendedores?.reduce((sum, v) => sum + v.parceiros_ativos, 0) || 0,
    totalLeads: vendedores?.reduce((sum, v) => sum + v.total_leads, 0) || 0,
    leadsEsteMes: vendedores?.reduce((sum, v) => sum + v.leads_30_dias, 0) || 0,
    totalPropostas: vendedores?.reduce((sum, v) => sum + v.propostas_enviadas, 0) || 0,
    propostasEsteMes: vendedores?.reduce((sum, v) => sum + v.propostas_30_dias, 0) || 0,
    totalContratos: vendedores?.reduce((sum, v) => sum + v.contratos_fechados, 0) || 0,
    contratosEsteMes: vendedores?.reduce((sum, v) => sum + v.contratos_30_dias, 0) || 0,
    valorTotal: vendedores?.reduce((sum, v) => sum + v.valor_contratos, 0) || 0
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2 sm:gap-3">
            <UserCog className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
            <span>{isAdmin ? 'Gerenciamento de Vendedores' : 'Meu Perfil de Vendedor'}</span>
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {isAdmin 
              ? 'Gerencie a equipe de vendas e acompanhe a performance da rede de parceiros'
              : 'Acompanhe sua performance e a rede de parceiros vinculados'
            }
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {isAdmin && (
            <Button 
              variant="outline" 
              size={isMobile ? "default" : "sm"}
              onClick={() => setShowFilters(!showFilters)}
              className={isMobile ? "w-full" : ""}
            >
              <FilterIcon className="h-4 w-4 sm:mr-2" />
              {!isMobile && (showFilters ? 'Ocultar Filtros' : 'Filtros')}
              {isMobile && (showFilters ? 'Ocultar' : 'Filtros')}
            </Button>
          )}
          <Button 
            variant="outline" 
            size={isMobile ? "default" : "sm"}
            onClick={refetch}
            disabled={loading}
            className={isMobile ? "w-full" : ""}
          >
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${loading ? 'animate-spin' : ''}`} />
            {!isMobile && 'Atualizar'}
          </Button>
          {isAdmin && (
            <Button 
              size={isMobile ? "default" : "sm"}
              onClick={() => setCadastroOpen(true)}
              className={isMobile ? "w-full" : ""}
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              Novo Vendedor
            </Button>
          )}
        </div>
      </div>

      {/* Métricas Gerais */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-primary/20 bg-gradient-to-br from-blue-50 to-white">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Total de Vendedores</CardTitle>
            <UserCog className="h-4 w-4 text-blue-600 flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-blue-600">{metricsAgregadas.totalVendedores}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metricsAgregadas.vendedoresAtivos} ativos
            </p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-gradient-to-br from-green-50 to-white">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Rede de Parceiros</CardTitle>
            <Users className="h-4 w-4 text-green-600 flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-green-600">{metricsAgregadas.totalParceiros}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {metricsAgregadas.parceirosAtivos} ativos
            </p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-gradient-to-br from-purple-50 to-white">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Leads Capturados</CardTitle>
            <TrendingUp className="h-4 w-4 text-purple-600 flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-purple-600">
              {metricsAgregadas.totalLeads}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              +{metricsAgregadas.leadsEsteMes} este mês
            </p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 bg-gradient-to-br from-orange-50 to-white">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Contratos Fechados</CardTitle>
            <DollarSign className="h-4 w-4 text-orange-600 flex-shrink-0" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold text-orange-600">
              {metricsAgregadas.totalContratos}
            </div>
            <p className="text-xs text-muted-foreground mt-1 break-words">
              {formatCurrency(metricsAgregadas.valorTotal)} em contratos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros (condicional - apenas para admin) */}
      {isAdmin && showFilters && (
        <VendedorFilters 
          filters={filters}
          onFiltersChange={setFilters}
        />
      )}

      {/* Busca */}
      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={isMobile ? "Buscar vendedores..." : "Buscar vendedores por nome ou email..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 text-sm sm:text-base"
          />
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600 text-sm">
              <strong>Erro:</strong> {error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Informação de resultados */}
      {!loading && !error && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-muted-foreground">
          <p className="break-words">
            {isAdmin 
              ? `Exibindo ${filteredVendedores.length} de ${vendedores?.length || 0} vendedores${searchTerm && !isMobile ? ` (filtrado por: "${searchTerm}")` : searchTerm && isMobile ? ' (filtrado)' : ''}`
              : 'Visualizando seu perfil de vendedor'
            }
          </p>
          {isAdmin && metricsAgregadas.totalVendedores === 0 && (
            <p className="text-blue-600 font-medium text-xs sm:text-sm">
              Comece cadastrando seu primeiro vendedor
            </p>
          )}
        </div>
      )}

      {/* Lista de Vendedores */}
      <VendedoresList 
        vendedores={filteredVendedores}
        loading={loading}
        onVendedorClick={setSelectedVendedor}
      />

      {/* Modal de Cadastro */}
      <CadastrarVendedor
        open={cadastroOpen}
        onClose={() => setCadastroOpen(false)}
        onSuccess={refetch}
      />

      {/* Modal de Detalhes */}
      <VendedorDetails
        vendedorId={selectedVendedor}
        onClose={() => setSelectedVendedor(null)}
        onUpdate={refetch}
      />
    </div>
  );
}

